import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from "react";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "sv", label: "Svenska" },
  { code: "no", label: "Norsk" },
  { code: "da", label: "Dansk" },
  { code: "fi", label: "Suomi" },
  { code: "de", label: "Deutsch" },
  { code: "nl", label: "Nederlands" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]["code"];

const COUNTRY_TO_LANGUAGE: Record<string, LanguageCode> = {
  SE: "sv",
  NO: "no",
  DK: "da",
  FI: "fi",
  DE: "de",
  AT: "de",
  CH: "de",
  NL: "nl",
  BE: "nl",
  FR: "fr",
  ES: "es",
  IT: "it",
};

/** Maps browser sub-language codes to our supported code */
const BROWSER_CODE_ALIASES: Record<string, LanguageCode> = {
  nb: "no",
  nn: "no",
};

export interface GeoOverrideInfo {
  detectedLang: LanguageCode;
  browserLang: LanguageCode;
}

interface LanguageContextValue {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  /** Set when geo-IP overrode the browser language. Null if no override happened. */
  geoOverride: GeoOverrideInfo | null;
  /** Dismiss the geo-override banner (accept the detected language). */
  acceptGeoOverride: () => void;
  /** Revert geo-override back to the browser language. */
  revertGeoOverride: () => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function resolveBrowserCode(raw: string): LanguageCode | undefined {
  const code = raw.slice(0, 2).toLowerCase();
  const alias = BROWSER_CODE_ALIASES[code];
  if (alias) return alias;
  const match = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  return match ? match.code : undefined;
}

function detectBrowserLanguage(): LanguageCode {
  if (typeof window !== "undefined" && (window.location.pathname.startsWith("/partner") || window.location.pathname.startsWith("/affiliates"))) {
    return "sv";
  }
  return resolveBrowserCode(navigator.language ?? "") ?? "en";
}

async function detectGeoLanguage(): Promise<LanguageCode | null> {
  try {
    const res = await fetch("https://api.country.is/", { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return null;
    const data = await res.json();
    const country = data?.country as string | undefined;
    if (!country) return null;
    return COUNTRY_TO_LANGUAGE[country.toUpperCase()] ?? "en";
  } catch {
    return null;
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const isFirstVisit = useRef(!localStorage.getItem("guestly_lang"));
  const userChosen = useRef(false);
  const browserLangRef = useRef<LanguageCode>(detectBrowserLanguage());

  const AFFILIATE_LANGUAGES: LanguageCode[] = ["sv", "en", "no", "da", "fi"];

  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const isAffiliatePath = typeof window !== "undefined" && window.location.pathname.startsWith("/affiliates");
    const stored = localStorage.getItem("guestly_lang");
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) {
      const storedLang = stored as LanguageCode;
      if (isAffiliatePath && !AFFILIATE_LANGUAGES.includes(storedLang)) {
        return "sv";
      }
      return storedLang;
    }
    return browserLangRef.current;
  });

  const [geoOverride, setGeoOverride] = useState<GeoOverrideInfo | null>(null);

  useEffect(() => {
    localStorage.setItem("guestly_lang", language);
    document.documentElement.lang = language;
  }, [language]);

  // Geo-detect on first visit only
  useEffect(() => {
    if (!isFirstVisit.current) return;
    if (localStorage.getItem("guestly_lang_geo")) return;

    detectGeoLanguage().then((geo) => {
      localStorage.setItem("guestly_lang_geo", "1");
      if (geo && !userChosen.current && geo !== browserLangRef.current) {
        setLanguageState(geo);
        setGeoOverride({ detectedLang: geo, browserLang: browserLangRef.current });
      }
    });
  }, []);

  const setLanguage = useCallback((code: LanguageCode) => {
    userChosen.current = true;
    setGeoOverride(null);
    setLanguageState(code);
  }, []);

  const acceptGeoOverride = useCallback(() => {
    setGeoOverride(null);
  }, []);

  const revertGeoOverride = useCallback(() => {
    if (geoOverride) {
      userChosen.current = true;
      setLanguageState(geoOverride.browserLang);
    }
    setGeoOverride(null);
  }, [geoOverride]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, geoOverride, acceptGeoOverride, revertGeoOverride }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
